<template>
  <FilterBox>
    <ProductFilterSkeleton v-if="showSkeleton" />
    <div :class="`filter-box__header ${showSkeleton ? 'd-none' : ''}`">
      <Button
        type="button"
        style-type="secondary"
        :disabled="
          !store.lastResponse ||
          (store.activeFilterCount === 0 && store.hasResults) ||
          !validFilters
        "
        @click="store.resetFilters()"
      >
        {{ resetFiltersText }}
      </Button>
    </div>
    <div
      v-if="store.hasResults"
      :class="`filter-box__content ${showSkeleton ? 'd-none' : ''}`"
    >
      <AccordionComponent open-multiple-items :item-heading-level="4">
        <template v-for="(aggregation, index) in store.aggregations">
          <AccordionItem
            v-if="store.filters[aggregation.attribute_code] || aggregation.options!.length > 0"
            :key="aggregation.attribute_code"
            :is-open="
              index < openSectionCount ||
              !isEmpty(store.filters[aggregation.attribute_code])
            "
            :data-testid="'productlist_filter_' + aggregation.attribute_code"
          >
            <template #title>{{ aggregation.label }}</template>
            <template
              v-for="(prices, i) in [
                (aggregation?.options ?? [])
                  .filter(isDefined)
                  .map((x) => Number(x.value))
              ]"
            >
              <!-- prices.length >= 1 allows the filters to be shown only if the products have prices -->
              <div
                v-if="
                  aggregation.attribute_code.includes('_range') &&
                  aggregation.frontend_input === 'price' &&
                  prices.length >= 1
                "
                :key="aggregation.attribute_code + i + 'range'"
              >
                <PrimevueSlider
                  :name="aggregation.attribute_code"
                  :label="aggregation.label ?? ''"
                  :min="
                    prices.length === 1 && Math.min(...prices) > 0
                      ? 0
                      : Math.min(...prices)
                  "
                  :max="
                    prices.length === 1 && Math.max(...prices) < 1
                      ? 1
                      : Math.max(...prices)
                  "
                  :possible-values="prices"
                  :values="store.filters[aggregation.attribute_code]"
                  :min-label="
                    $textDictionary[`productsList.filters.min.label`] ?? 'min'
                  "
                  :max-label="
                    $textDictionary[`productsList.filters.max.label`] ?? 'max'
                  "
                  :disabled="store.staticFilters[aggregation.attribute_code!] != null || store.loading"
                  @change="
                    (min, max) =>
                      store.handleFormValueChange(aggregation.attribute_code, [
                        min.toString(),
                        max.toString()
                      ])
                  "
                />
              </div>
              <div
                v-else-if="
                  aggregation.attribute_code === 'price' && prices.length >= 1
                "
                :key="aggregation.attribute_code + i + 'price'"
              >
                <PrimevueSlider
                  :name="aggregation.attribute_code"
                  :label="aggregation.label ?? ''"
                  :min="
                    prices.length === 1 && Math.min(...prices) > 0
                      ? 0
                      : Math.min(...prices)
                  "
                  :max="
                    prices.length === 1 && Math.max(...prices) < 1
                      ? 1
                      : Math.max(...prices)
                  "
                  :possible-values="prices"
                  :values="store.filters[aggregation.attribute_code]"
                  :min-label="
                    $textDictionary['productsList.filters.price.min.label'] ??
                    ''
                  "
                  :max-label="
                    $textDictionary['productsList.filters.price.max.label'] ??
                    ''
                  "
                  :currency-formatter="currencyFormatter"
                  :disabled="store.staticFilters[aggregation.attribute_code!] != null || store.loading"
                  @change="
                    (min, max) =>
                      store.handleFormValueChange(aggregation.attribute_code, [
                        min.toString(),
                        max.toString()
                      ])
                  "
                />
              </div>
              <ProductFilter
                v-else-if="store.filters[aggregation.attribute_code] || aggregation.options!.length > 0"
                :key="aggregation.attribute_code + i"
                :filter-type="getFilterType(aggregation)"
                :filter-label="aggregation.label ?? '<<no_label>>'"
                :filter-name="aggregation.attribute_code!"
                :filter-value="store.filters[aggregation.attribute_code!] ?? []"
                :filter-options="createOptions(aggregation.options)"
              />
            </template>
            <LinkElement
              v-if="aggregation.attribute_code === 'interface' && inalink"
              :link="inalink"
              :title="$textDictionary['pdp.filters.ina.linktooltip']"
              :target-blank="false"
              class="text--small"
              @click="pushAnalyticsEventOnInALinkKlick()"
              >{{ $textDictionary[`pdp.filters.ina.linktext`] }}</LinkElement
            >

            <LensSelectorInModal
              v-if="
                aggregation.attribute_code === 'min_focallength_range' &&
                lensSelectorInModal
              "
              :tool="lensSelectorInModal"
              :ls-link-type="'filter'"
              @focal-length="
                (minValue, maxValue) => setFocalLength(minValue, maxValue)
              "
            />
          </AccordionItem>
        </template>
      </AccordionComponent>
    </div>
  </FilterBox>
</template>

<script setup lang="ts">
import {
  AccordionComponent,
  AccordionItem,
  FilterBox,
  LinkElement,
  Button
} from '@hypercodestudio/basler-components';
import PrimevueSlider from './PrimevueSlider.vue';

import ProductFilter from './ProductFilter.vue';
import ProductFilterSkeleton from './skeletons/ProductFilterSkeleton.vue';
import type {
  ProductAggregationFragment,
  ProductAggregationOptionFragment
} from '~/lib/Shop/generated/schema';
import { isDefined } from '~/utils/guards/isDefined';
import { CATEGORY_ID } from '~/utils/shop/constants';
import { useProductFilterStore } from '~/stores/productFilterStore/productFilterStore';
import LensSelectorInModal from '../lib/Tools/LensSelectorInModal.vue';
import type { ITechnicalComponent } from '~/lib/ContentfulService';
import { isEmpty } from '~/utils/isEmpty';

type IndexedProductAggregations = Record<string, ProductAggregationFragment>;

const { $resolvedLocale } = useNuxtApp();

interface Props {
  openSectionCount?: number;
  showInaLink: boolean;
  lensSelectorInModal?: ITechnicalComponent;
}

const props = withDefaults(defineProps<Props>(), {
  openSectionCount: 5,
  lensSelectorInModal: undefined
});

// Not properly typed in schema. the values should equal UiInputTypeEnum
function getFilterType(
  aggregation: ProductAggregationFragment
): 'checkbox' | 'radio' {
  // category should be displayed as select. can not be configured in
  // magento.
  // @see https://gcp.baslerweb.com/jira/browse/WEB2-2090
  if (aggregation.attribute_code === CATEGORY_ID) {
    return 'radio';
  }

  // all filters should be multi-selects by default
  // @see https://gcp.baslerweb.com/jira/browse/WEB2-2343
  if (
    aggregation.frontend_input === 'select' ||
    aggregation.frontend_input === 'multiselect'
  ) {
    return 'checkbox';
  }
  // text fields can only have single values
  if (
    aggregation.frontend_input === 'text' ||
    aggregation.frontend_input === 'boolean'
  ) {
    return 'radio';
  }

  return 'checkbox';
}

const { $textDictionary, $analytics } = useNuxtApp();
const store = useProductFilterStore();
const showSkeleton = computed(
  () => store.lastResponse == null && store.loading
);

const inalink = computed(() => {
  const showlink = store.aggregations.find(
    (aggregation) =>
      aggregation.attribute_code === 'interface' &&
      aggregation.options &&
      aggregation.options.length > 1
  );

  if (store.filters.interface === undefined && props.showInaLink && showlink) {
    return (
      $textDictionary.value['pdp.filters.ina.linkhref'] +
      '?referer=' +
      window.location.href
    );
  }
});

function pushAnalyticsEventOnInALinkKlick() {
  $analytics.pushToDataLayer({
    action: 'click_ina_link_filter',
    category: 'filter_link',
    event: 'click_Internal',
    label: 'InA Link'
  });
}

function setFocalLength(minValue: string, maxValue: string) {
  store.handleFormValueChange('min_focallength_range', [minValue, maxValue]);
}

const validFilters = computed(() => {
  const indexedAggregations = getIndexedProductAggregations(store.aggregations);
  const validFilters: Record<string, string[]> = {};
  Object.entries(store.visibleFilters).forEach(([key, value]) => {
    if (indexedAggregations[key]) {
      validFilters[key] = value;
    }
  });
  return Object.keys(validFilters).length;
});

const resetFiltersText = computed(() => {
  if (store.activeFilterCount === 0 || validFilters.value === 0) {
    return $textDictionary.value['pdp.filters.resetFiltersNone.label'];
  }

  if (store.activeFilterCount === 1 && validFilters.value) {
    return $textDictionary.value['pdp.filters.resetFiltersOne.label'];
  }

  return $textDictionary.value['pdp.filters.resetFiltersOther.label']?.replace(
    '{count}',
    String(validFilters.value)
  );
});

const currencyFormatter = computed(() => {
  return {
    locale: $resolvedLocale.value?.toString() ?? 'en-US',
    currency: store.currencyCode ?? 'EUR'
  };
});

function getIndexedProductAggregations(
  aggregations: ProductAggregationFragment[]
) {
  return aggregations.reduce<IndexedProductAggregations>((prev, curr) => {
    if (curr.attribute_code) {
      return { ...prev, [curr.attribute_code]: curr };
    }
    return { ...prev };
  }, {});
}

function createOptions(
  options?: ReadonlyArray<ProductAggregationOptionFragment | null> | null
): Record<string, string> {
  const defaultValue: Record<string, string> = {};

  return (
    options?.reduce((collector, option) => {
      if (!option) {
        return collector;
      }

      const label =
        typeof option.count === 'number'
          ? `${option.label} (${option.count})`
          : option.label ?? '';

      return {
        ...collector,
        [option.value]: label
      };
    }, defaultValue) ?? defaultValue
  );
}
</script>

<style>
.filter-box__header {
  min-height: 41px;
}
</style>
